/**
 * This code was  originally copied from the lexical playground source code on github
 * And so it doesn't follow our code style
 * https://github.com/facebook/lexical/tree/main/packages/lexical-playground
 *
 */
import { addClassNamesToElement } from "@lexical/utils";
import { isContractValidationPage, isSetupPage } from "helpers/utils";
import { t } from "i18next";
import type {
    DOMConversionMap,
    DOMConversionOutput,
    DOMExportOutput,
    EditorConfig,
    LexicalNode,
    NodeKey,
    SerializedElementNode,
} from "lexical";
import { ElementNode } from "lexical";

import type { RepeatBy } from "./repeatBy";

type SerializedRepeatableContentNode = SerializedElementNode & {
    beneficiaryName?: string;
    beneficiaryIndex?: string;
    repeatBy?: RepeatBy;
    articleName?: string;
    articleIndex?: string;
};

export function convertRepeatableContentElement(_domNode: HTMLElement): DOMConversionOutput | null {
    const node = $createRepeatableContentNode();
    return {
        node,
    };
}

/**
 * The RepeatableContentNode contains the block content with variables
 */
export class RepeatableContentNode extends ElementNode {
    __beneficiaryName?: string;
    /**
     * Save beneficiary index to correctly setup variables on contract validation page
     */
    __beneficiaryIndex?: string;
    __repeatBy: RepeatBy = "beneficiary";
    __articleName?: string;
    /**
     * Position of this copy among the work articles, aligned with `articles.{index}.*`.
     */
    __articleIndex?: string;

    constructor(
        beneficiaryName?: string,
        beneficiaryIndex?: string,
        repeatBy: RepeatBy = "beneficiary",
        articleName?: string,
        articleIndex?: string,
        key?: NodeKey,
    ) {
        super(key);
        this.__beneficiaryName = beneficiaryName;
        this.__beneficiaryIndex = beneficiaryIndex;
        this.__repeatBy = repeatBy;
        this.__articleName = articleName;
        this.__articleIndex = articleIndex;
    }

    getRepeatBy(): RepeatBy {
        return this.getLatest().__repeatBy ?? "beneficiary";
    }

    getBeneficiaryIndex(): string | undefined {
        return this.getLatest().__beneficiaryIndex;
    }

    getArticleIndex(): string | undefined {
        return this.getLatest().__articleIndex;
    }

    repeatLabel(): string {
        const self = this.getLatest();
        if (self.__repeatBy === "article") {
            return self.__articleName || t("RichEditor.RepeatableByArticle");
        }
        return self.__beneficiaryName || t("RichEditor.RepeatableByBeneficiary");
    }

    static getType(): string {
        return "repeatable-content";
    }

    static clone(node: RepeatableContentNode): RepeatableContentNode {
        return new RepeatableContentNode(
            node.__beneficiaryName,
            node.__beneficiaryIndex,
            node.__repeatBy,
            node.__articleName,
            node.__articleIndex,
            node.__key,
        );
    }

    createDOM(_config: EditorConfig): HTMLElement {
        const dom = document.createElement("div");
        if (!isContractValidationPage()) {
            dom.setAttribute("data-content", this.repeatLabel());
        }
        if (isContractValidationPage() && this.__repeatBy === "article") {
            dom.setAttribute("data-article-index", this.__articleIndex ?? "");
        }
        if (isContractValidationPage() && this.__repeatBy !== "article") {
            dom.setAttribute("data-beneficiary-index", this.__beneficiaryIndex ?? "");
        }
        dom.classList.add("Repeatable__content");
        if (this.__repeatBy === "article") dom.classList.add("Repeatable__content--article");
        return dom;
    }

    updateDOM(prevNode: RepeatableContentNode, dom: HTMLElement): boolean {
        return false;
    }

    static importDOM(): DOMConversionMap | null {
        return {
            div: (domNode: HTMLElement) => {
                if (!domNode.hasAttribute("data-lexical-repeatable-content")) {
                    return null;
                }
                return {
                    conversion: convertRepeatableContentElement,
                    priority: 2,
                };
            },
        };
    }

    exportDOM(): DOMExportOutput {
        const element = document.createElement("div");
        element.setAttribute("data-lexical-repeatable-content", "true");
        element.setAttribute("data-content", this.repeatLabel());
        element.setAttribute("data-repeat-by", this.__repeatBy);
        element.setAttribute("data-beneficiary-index", this.__beneficiaryIndex ?? "");
        element.setAttribute("data-article-index", this.__articleIndex ?? "");
        return { element };
    }

    static importJSON(serializedNode: SerializedRepeatableContentNode): RepeatableContentNode {
        const { beneficiaryName, beneficiaryIndex, repeatBy, articleName, articleIndex } = serializedNode;
        return new RepeatableContentNode(
            beneficiaryName,
            beneficiaryIndex,
            repeatBy ?? "beneficiary",
            articleName,
            articleIndex,
        );
    }

    isShadowRoot(): boolean {
        return true;
    }

    exportJSON(): SerializedRepeatableContentNode {
        return {
            ...super.exportJSON(),
            type: "repeatable-content",
            version: 1,
            beneficiaryName: this.__beneficiaryName,
            beneficiaryIndex: this.__beneficiaryIndex,
            repeatBy: this.__repeatBy,
            articleName: this.__articleName,
            articleIndex: this.__articleIndex,
        };
    }
}

export function $createRepeatableContentNode(
    beneficiaryName?: string,
    beneficiaryIndex?: string,
    repeatBy: RepeatBy = "beneficiary",
    articleName?: string,
    articleIndex?: string,
): RepeatableContentNode {
    return new RepeatableContentNode(beneficiaryName, beneficiaryIndex, repeatBy, articleName, articleIndex);
}

export function $isRepeatableContentNode(node: LexicalNode | null | undefined): node is RepeatableContentNode {
    return node instanceof RepeatableContentNode;
}
